/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2021-2025 (original work) Open Assessment Technologies SA;
 */
import env from '@/config/env';
import request from 'tao-core-sdk/core/fetchRequest';
import { getEndpointUrl } from '@/core/api/apiRequest';
import { accessToken as accessTokenStore } from '@/store/accessToken';
import { ltiClaims } from '@/core/utils/consts';

let accessToken = null;

function createRequestHeaders(refreshUrlHref, refreshTokenId) {
    const headers = {};
    const refreshUrl = new URL(refreshUrlHref);
    // A precaution measure that would fall back to cookie-based auth when session-storage auth had been enabled and then switched back to cookie mode
    if (refreshUrl.origin !== window.location.origin) {
        return headers;
    }

    const refreshToken = sessionStorage.getItem(refreshTokenId);
    if (refreshToken) {
        try {
            headers['refresh-token'] = JSON.parse(refreshToken);
        } catch {
            // ignore invalid refresh token values
        }
    }
    return headers;
}

/**
 * Checks if current accessToken is outdated. If it is, a new accessToken is requested
 * and overwrrites accessToken module property with it
 * @returns {Promise} - promise containing the resolution of refreshAccessToken request
 */
export function refreshAccessToken() {
    return new Promise((resolve, reject) => {
        const urlParams = new URLSearchParams(window.location.search);
        let tenantId = urlParams.get('tenantId');
        let context = urlParams.get('context');

        const refreshUrl = getEndpointUrl({ domain: 'LTI_BACKEND_SERVICE', endpoint: 'refreshToken' }, {}, {});

        if (tenantId == null || context == null) {
            const jwtToken = parseJwtPayload(sessionStorage.getItem('accessToken'));
            context = jwtToken && jwtToken[ltiClaims.context] && jwtToken[ltiClaims.context]['id'];
            tenantId = jwtToken && jwtToken['tenant_id'];
        }

        const refreshTokenId = `${tenantId}_${context}`;
        const headers = createRequestHeaders(refreshUrl, refreshTokenId);
        request(refreshUrl, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: JSON.stringify({ refreshTokenId })
        })
            .then(response => {
                accessToken = response.accessToken;
                setAccessToken();
                resolve();
            })
            .catch(() => {
                reject({ message: 'Token not available and cannot be refreshed' });
            });
    });
}

/**
 * Decodes the payload (middle section) of a JWT token
 * @param {String} token - JWT token, 'xxxxx.yyyyy.zzzzz' format
 * @returns {Object} JWT payload
 */
export function parseJwtPayload(token) {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
        return null;
    }
}

/**
 * Sets accessToken module property if not already present.
 * Checks first for JWT in sessionStorage.
 * If not present and on local development, JWT_TOKEN from .env is used instead.
 * @returns {string | null} - JWT containing LTI proctor claims
 */
export async function setAccessToken() {
    if (accessToken === null) {
        // First we check sessionStorage
        accessToken = sessionStorage.getItem('accessToken');

        // if accessToken is not provided in URL or sessionStorage, we check .env only for local development
        if (accessToken === null && env('NODE_ENV') === 'local') {
            accessToken = env('JWT_TOKEN');
        }
    }
    if (accessToken) {
        sessionStorage.setItem('accessToken', accessToken);
        accessTokenStore.set(accessToken);
    }

    return accessToken;
}
